<template>
  <div class="card p-shadow-app">
    <h1>Administração</h1>
    <Toast :style="{ width: $isMobile() ? '85vw' : '' }" />
    <ConfirmDialog></ConfirmDialog>
    
    <Loading
      :active.sync="loading"
      :isFullPage="false"
      :zIndex="9000"
    ></Loading>

    <div class="p-fluid p-formgrid p-grid p-mt-4" style="max-width: 800px; margin: 0 auto;">
      <div v-for="config in configs" :key="config.code" class="p-field p-col-12 p-mt-3">
        <label :for="config.code" style="font-weight: 600; display: block; margin-bottom: 6px;">
          {{ config.description }}
        </label>
        
        <div v-if="config.code === 'default_vat_rate'">
          <Dropdown
            :id="config.code"
            v-model="config.tempValue"
            :options="taxRuleGroups"
            optionValue="id"
            optionLabel="name"
            :filter="true"
            :showClear="true"
            placeholder="Selecione um grupo de taxa (PrestaShop)..."
          />
        </div>
        <div v-else-if="config.code === 'default_sales_margin'" class="p-inputgroup">
          <InputNumber
            :id="config.code"
            v-model.number="config.tempValue"
            mode="decimal"
            :min="0"
            :max="100"
            suffix=" %"
            placeholder="Introduza a percentagem..."
          />
        </div>
        <div v-else-if="config.code === 'default_weight'" class="p-inputgroup">
          <InputNumber
            :id="config.code"
            v-model.number="config.tempValue"
            mode="decimal"
            :min="0"
            :max="9999999.999"
            :minFractionDigits="3"
            :maxFractionDigits="3"
            :useGrouping="false"
            locale="pt-PT"
            placeholder="Introduza o peso..."
          />
        </div>
        <div v-else-if="config.code === 'prestashop_home_category_id'" class="p-inputgroup">
          <InputNumber
            :id="config.code"
            v-model="config.tempValue"
            :min="1"
            :useGrouping="false"
            placeholder="Introduza o ID da categoria Início..."
          />
        </div>
        <div v-else-if="config.code === 'default_part_image' || config.code === 'default_equipment_image'">
          <div v-if="config.tempValue" class="p-d-flex p-flex-wrap p-ai-center p-jc-start">
            <AuthenticatedImage
              :file="config.tempValue"
              alt="Imagem por Defeito"
              height="100"
              class="p-mr-3"
            />
            <Button
              icon="pi pi-trash"
              class="p-button-sm p-button-danger"
              @click="removeFile(config.code)"
              type="button"
              v-tooltip="'Remover Imagem'"
              style="width: auto;"
            />
          </div>
          <div v-else>
            <FileUpload
              :ref="config.code"
              :name="config.code + '[]'"
              accept="image/gif,image/jpg,image/jpeg,image/pjpeg,image/png,image/x-png"
              invalidFileTypeMessage="Ficheiro inválido. Apenas aceita GIF, JPG, JPEG ou PNG"
              :previewWidth="100"
              :multiple="false"
              :fileLimit="1"
              :showUploadButton="false"
              :showCancelButton="false"
            >
              <template #empty>
                <div class="drag-drop-files">
                  <p>Arraste a imagem por defeito ou clique para selecionar</p>
                </div>
              </template>
            </FileUpload>
          </div>
        </div>
        <div v-else class="p-inputgroup">
          <InputText
            :id="config.code"
            v-model="config.tempValue"
            type="text"
            placeholder="Introduza o valor..."
          />
        </div>
      </div>

      <div class="p-field p-col-12 p-mt-4 p-d-flex p-jc-end">
        <Button
          label="Guardar Configurações"
          icon="pi pi-save"
          class="p-button-success p-button-sm p-mr-2"
          style="width: auto;"
          @click="saveConfigurations"
          :disabled="loading"
        />
      </div>
    </div>
  </div>
</template>

<script>
import "vue-loading-overlay/dist/vue-loading.css";
import sparsService from "../../services/spars.service";
import AuthenticatedImage from "../../components/elements/AuthenticatedImage.vue";

export default {
  name: "SparsAdministration",
  components: {
    AuthenticatedImage,
  },
  data() {
    return {
      loading: false,
      configs: [],
      taxRuleGroups: [],
    };
  },
  async created() {
    this.loading = true;
    try {
      const taxGroupsResponse = await sparsService.getTaxRuleGroups();
      this.taxRuleGroups = taxGroupsResponse?.tax_rule_groups || [];
      await this.loadConfigurations();
    } catch (error) {
      console.error(error);
      this.$toast.add({
        severity: "error",
        summary: "Erro",
        detail: "Erro ao inicializar as configurações.",
        life: 3000,
      });
    } finally {
      this.loading = false;
    }
  },
  deactivated() {
    document.body.classList.remove('p-overflow-hidden');
  },
  beforeDestroy() {
    document.body.classList.remove('p-overflow-hidden');
  },
  methods: {
    async loadConfigurations() {
      this.loading = true;
      try {
        const res = await sparsService.getConfigurations();
        if (res) {
          this.configs = res.map(item => {
            let tempValue = item.value;
            if (item.code === 'default_vat_rate') {
              tempValue = item.value ? parseInt(item.value, 10) : null;
            } else if (item.code === 'prestashop_home_category_id') {
              tempValue = item.value ? parseInt(item.value, 10) : null;
            } else if (['default_sales_margin', 'default_weight'].includes(item.code)) {
              tempValue = parseFloat(item.value) || 0;
            } else {
              tempValue = item.value || "";
            }
            return {
              ...item,
              tempValue
            };
          });
        }
      } catch (error) {
        console.error(error);
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Erro ao carregar as configurações.",
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    async saveConfigurations() {
      this.loading = true;
      try {
        let formData = new FormData();

        const textConfigs = this.configs.filter(item => !['default_part_image', 'default_equipment_image'].includes(item.code));
        formData.append("configurations", JSON.stringify(textConfigs.map(item => ({
          code: item.code,
          value: item.tempValue !== null && item.tempValue !== undefined ? String(item.tempValue) : ""
        }))));

        ['default_part_image', 'default_equipment_image'].forEach(code => {
          const fileUploadRef = this.$refs[code];
          const refInstance = Array.isArray(fileUploadRef) ? fileUploadRef[0] : fileUploadRef;
          if (refInstance && refInstance.files && refInstance.files.length > 0) {
            formData.append(code, refInstance.files[0]);
          }
        });

        const res = await sparsService.bulkUpdateConfigurations(formData);
        if (res) {
          this.$toast.add({
            severity: "success",
            summary: "Sucesso",
            detail: "Configurações atualizadas com sucesso!",
            life: 3000,
          });
          await this.loadConfigurations();
        } else {
          this.$toast.add({
            severity: "error",
            summary: "Erro",
            detail: "Erro ao atualizar as configurações.",
            life: 3000,
          });
        }
      } catch (error) {
        console.error(error);
        const detail = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : "Erro de rede ao guardar as configurações.";
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail,
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    removeFile(code) {
      this.$confirm.require({
        header: "Remover Imagem",
        message: "Tem a certeza que pretende remover esta imagem por defeito?",
        icon: "pi pi-question-circle",
        acceptLabel: "Sim",
        acceptIcon: "pi pi-check",
        acceptClass: "p-button-success p-button p-component",
        rejectLabel: "Não",
        rejectIcon: "pi pi-times",
        rejectClass: "p-button-danger p-button p-component",
        accept: () => {
          this.loading = true;
          sparsService
            .removeConfigurationFile(code)
            .then(async (response) => {
              if (!response) {
                this.$toast.add({
                  severity: "error",
                  summary: "Erro",
                  detail: "Ocorreu um erro ao remover a imagem.",
                  life: 3000,
                });
                return;
              }
              this.$toast.add({
                severity: "success",
                summary: "Sucesso",
                detail: "Imagem removida com sucesso",
                life: 3000,
              });
              await this.loadConfigurations();
            })
            .catch((error) => {
              console.error(error);
              this.$toast.add({
                severity: "error",
                summary: "Erro",
                detail: "Erro ao remover a imagem.",
                life: 3000,
              });
            })
            .finally(() => {
              this.loading = false;
            });
        },
        reject: () => {
          return;
        },
      });
    }
  }
};
</script>

<style scoped>
.p-shadow-app {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  padding: 2rem;
  background: var(--surface-card);
}
</style>
